import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Chip,
  Stack,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import { scheduleService, type Schedule } from '../services/schedule.service';
import { useAuthStore } from '@/stores/authStore';
import { useErrorStore } from '@/stores/errorStore';
import { userService } from '../services/user.service';
import { watcherRegistryService, type WatcherClient } from '../services/watcher-registry.service';
import type { User } from '@/types/user.types';
import { useFieldErrors, type FieldErrors } from '@/utils/formErrors';

type Frequency = 'daily' | 'weekly' | 'monthly' | 'custom';

const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];
const DEFAULT_CUSTOM_DAYS = [1, 2, 3, 4, 5];
const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

// The zone the user is typing times in. Stored on the expression as a
// CRON_TZ= prefix so the backend evaluates the fields in that zone.
const browserZone = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Taipei';
  } catch {
    return 'Asia/Taipei';
  }
})();

// Legacy rows (no CRON_TZ= prefix) were written in UTC with the hour shifted
// by hand; show them back in UTC+8 the way the old form did.
const legacyUTCToLocal = (utcHour: number): number => {
  let localHour = utcHour + 8;
  if (localHour >= 24) localHour -= 24;
  return localHour;
};

interface CronForm {
  zone: string | null; // null = legacy UTC row
  frequency: Frequency;
  hour: string;
  minute: string;
  days: number[]; // custom: 0 = Sunday
}

/**
 * cron → form. Recognises the shapes the form writes (daily / weekly on
 * Sunday / monthly on the 1st / custom weekday list), with or without the
 * CRON_TZ= prefix. Anything else (step or range fields written by hand)
 * returns null and is shown raw.
 */
function parseCron(cron: string): CronForm | null {
  const parts = cron.trim().split(/\s+/);
  let zone: string | null = null;
  if (parts.length === 6 && /^(CRON_TZ|TZ)=/.test(parts[0])) {
    zone = parts[0].slice(parts[0].indexOf('=') + 1);
    parts.shift();
  }
  if (parts.length !== 5) return null;
  const [m, h, dom, mon, dow] = parts;
  if (!/^\d{1,2}$/.test(m) || !/^\d{1,2}$/.test(h) || mon !== '*') return null;
  const hourNum = zone ? parseInt(h, 10) : legacyUTCToLocal(parseInt(h, 10));
  const base = { zone, hour: hourNum.toString().padStart(2, '0'), minute: m.padStart(2, '0') };
  if (dom === '1' && dow === '*') return { ...base, frequency: 'monthly', days: [] };
  if (dom === '*' && dow === '0') return { ...base, frequency: 'weekly', days: [0] };
  if (dom === '*' && dow === '*') return { ...base, frequency: 'daily', days: [] };
  if (dom === '*' && /^[0-6](,[0-6])*$/.test(dow)) {
    return { ...base, frequency: 'custom', days: dow.split(',').map(Number) };
  }
  return null;
}

/** form → cron, in the user's zone (QA #47/#48). */
function toCronExpression(freq: Frequency, h: string, m: string, days: number[]): string {
  const dom = freq === 'monthly' ? '1' : '*';
  const dow = freq === 'weekly' ? '0' : freq === 'custom' ? [...days].sort((a, b) => a - b).join(',') : '*';
  return `CRON_TZ=${browserZone} ${parseInt(m, 10)} ${parseInt(h, 10)} ${dom} * ${dow}`;
}

function describeFrequency(cron: string): string {
  const f = parseCron(cron);
  if (!f) return cron;
  switch (f.frequency) {
    case 'daily':
      return '每天';
    case 'weekly':
      return '每週日';
    case 'monthly':
      return '每月 1 號';
    default:
      return `自訂：週${f.days.map((d) => WEEKDAY_LABELS[d]).join('、')}`;
  }
}

function describeTime(cron: string): string {
  const f = parseCron(cron);
  if (!f) return '—';
  return `${f.hour}:${f.minute}${f.zone ? '' : '（UTC+8）'}`;
}

export default function Schedule() {
  const currentUser = useAuthStore((s) => s.user);
  const showError = useErrorStore((s) => s.showError);
  const showSuccess = useErrorStore((s) => s.showSuccess);
  const [userFilter, setUserFilter] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [frequency, setFrequency] = useState<Frequency>('daily');
  const [hour, setHour] = useState('02');
  const [minute, setMinute] = useState('00');
  // QA #47: 自訂 = 一週內挑幾天（0 = 週日）。預設週一到週五。
  const [customDays, setCustomDays] = useState<number[]>(DEFAULT_CUSTOM_DAYS);
  const [paths, setPaths] = useState<string[]>(['/data/documents']);
  const [name, setName] = useState('');
  // QA #25 / #68: field problems (own checks and the backend's 400
  // VALIDATION_ERROR fields) are marked on the input, keyed by the request's
  // JSON keys; `days` is the form's own.
  const form = useFieldErrors();
  const [watchers, setWatchers] = useState<WatcherClient[]>([]);
  const [selectedWatcherId, setSelectedWatcherId] = useState<string>('');

  // 立即執行排程對話框狀態
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [scheduleToTrigger, setScheduleToTrigger] = useState<{ id: string; name: string } | null>(null);
  const [triggering, setTriggering] = useState(false);

  // 刪除排程對話框狀態
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 編輯模式：非 null 時表單「儲存」走 PUT /schedules/:id（A-08 / F-31）
  const [editingId, setEditingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const addPath = () => setPaths([...paths, '']);
  const removePath = (index: number) => setPaths(paths.filter((_, i) => i !== index));
  const updatePath = (index: number, value: string) => {
    const newPaths = [...paths];
    newPaths[index] = value;
    setPaths(newPaths);
  };

  useEffect(() => {
    if (currentUser?.role === 'admin' || currentUser?.role === 'sub_admin') {
      userService.listUsers({ page_size: 100 }).then((res) => {
        setAvailableUsers(res?.items ?? []);
      }).catch(console.error);
    }
  }, [currentUser]);

  useEffect(() => {
    watcherRegistryService.listWatchers().then((list) => {
      setWatchers(list.filter((w) => w.is_enabled));
      if (list.length > 0 && !selectedWatcherId) {
        setSelectedWatcherId(list.find((w) => w.is_enabled)?.id || list[0].id);
      }
    }).catch(console.error);
  }, []);

  useEffect(() => {
    loadSchedules();
  }, [userFilter]);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const params = userFilter ? { user_id: userFilter } : undefined;
      const data = await scheduleService.listSchedules(params);
      // Ensure data is an array
      setSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load schedules:', error);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    // QA #25: required-field problems show inline on the field, not as a
    // Snackbar, and block before the backend 400.
    const filteredPaths = paths.filter((p) => p.trim() !== '').map((p) => p.trim());
    const nextErrors: FieldErrors = {};
    if (!name.trim()) nextErrors.name = '請輸入排程名稱（例如：每日文件備份）';
    if (filteredPaths.length === 0) nextErrors.source_paths = '請至少輸入一個要備份的路徑（例如 /data/documents）';
    if (!selectedWatcherId) nextErrors.watcher_id = '請選擇要執行這個排程的 File Watcher';
    if (frequency === 'custom' && customDays.length === 0) nextErrors.days = '自訂頻率請至少選擇一天';
    if (Object.keys(nextErrors).length > 0) {
      form.setErrors(nextErrors);
      return;
    }
    form.clear();

    try {
      const cronExpression = toCronExpression(frequency, hour, minute, customDays);
      const payload = {
        name: name.trim(),
        source_paths: filteredPaths,
        cron_expression: cronExpression,
        watcher_id: selectedWatcherId,
      };
      if (editingId) {
        await scheduleService.updateSchedule(editingId, payload);
        showSuccess('排程已更新');
      } else {
        await scheduleService.createSchedule(payload);
        showSuccess('排程設定已儲存！');
      }
      resetForm();
      loadSchedules();
    } catch (error) {
      console.error('Failed to save schedule:', error);
      // Field problems land on the field; only the rest goes to the snackbar.
      const general = form.apply(error, {}, editingId ? '更新排程失敗，請稍後再試' : '儲存排程失敗，請稍後再試');
      if (general) showError(general);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    form.clear();
    setName('');
    setPaths(['/data/documents']);
    setFrequency('daily');
    setHour('02');
    setMinute('00');
    setCustomDays(DEFAULT_CUSTOM_DAYS);
  };

  // 把一列載入表單進入編輯模式（parseCron 認不得的手寫表達式退回每天 02:00）
  const editingRow = editingId ? schedules.find((s) => s.id === editingId) : undefined;
  const boundWatcherMissing = Boolean(
    editingRow?.watcher_id &&
      selectedWatcherId === editingRow.watcher_id &&
      !watchers.some((w) => w.id === selectedWatcherId),
  );

  const startEdit = (row: Schedule) => {
    const sourcePaths = row.source_paths && row.source_paths.length > 0
      ? row.source_paths
      : row.source_path ? [row.source_path] : [''];
    const parsed = parseCron(row.cron_expression);
    setEditingId(row.id);
    form.clear();
    setName(row.name);
    setPaths([...sourcePaths]);
    setFrequency(parsed?.frequency ?? 'daily');
    setHour(parsed?.hour ?? '02');
    setMinute(parsed ? MINUTES.includes(parsed.minute) ? parsed.minute : '00' : '00');
    setCustomDays(parsed && parsed.frequency === 'custom' && parsed.days.length > 0 ? parsed.days : DEFAULT_CUSTOM_DAYS);
    if (row.watcher_id) setSelectedWatcherId(row.watcher_id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 啟用 / 停用（A-09）：後端 enable 會重算 next_run_at，disable 保留
  const toggleEnabled = async (row: Schedule, enabled: boolean) => {
    setTogglingId(row.id);
    try {
      await scheduleService.setScheduleEnabled(row.id, enabled);
      showSuccess(enabled ? `已啟用「${row.name}」` : `已停用「${row.name}」`);
      loadSchedules();
    } catch (error) {
      console.error('Failed to toggle schedule:', error);
      showError(enabled ? '啟用失敗！' : '停用失敗！');
    } finally {
      setTogglingId(null);
    }
  };

  // 開啟立即執行對話框
  const openTriggerDialog = (scheduleId: string, scheduleName: string) => {
    setScheduleToTrigger({ id: scheduleId, name: scheduleName });
    setTriggerDialogOpen(true);
  };

  // 確認立即執行
  const handleTriggerSchedule = async () => {
    if (!scheduleToTrigger) return;
    try {
      setTriggering(true);
      const result = await scheduleService.triggerSchedule(scheduleToTrigger.id);
      setTriggerDialogOpen(false);
      setScheduleToTrigger(null);
      showSuccess(`${result.message}（排程：${result.schedule_name}，路徑：${result.source_path}）`);
    } catch (error) {
      console.error('Failed to trigger schedule:', error);
      // ADR-044 D4: 409 WATCHER_OFFLINE — nothing would pick the run up.
      const apiErr = error as { code?: string; message?: string };
      if (apiErr?.code === 'WATCHER_OFFLINE') {
        showError('綁定的 File Watcher 離線（5 分鐘內無心跳）、已停用或未綁定，未觸發。請確認 agent 正在執行後再試。');
      } else {
        showError(`執行失敗：${apiErr?.message ?? '未知錯誤'}`);
      }
    } finally {
      setTriggering(false);
    }
  };

  // 開啟刪除對話框
  const openDeleteDialog = (id: string) => {
    setScheduleToDelete(id);
    setDeleteDialogOpen(true);
  };

  // 確認刪除
  const handleDeleteSchedule = async () => {
    if (!scheduleToDelete) return;
    try {
      setDeleting(true);
      await scheduleService.deleteSchedule(scheduleToDelete);
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
      loadSchedules();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      showError('刪除失敗！');
    } finally {
      setDeleting(false);
    }
  };


  const columns = useMemo<GridColDef<Schedule>[]>(
    () => [
      { field: 'name', headerName: '排程名稱', flex: 1 },
      {
        field: 'source_paths',
        headerName: '備份路徑',
        flex: 1.2,
        renderCell: (params) => {
          const sourcePaths = params.row.source_paths || (params.row.source_path ? [params.row.source_path] : []);
          if (sourcePaths.length === 0) return '-';
          if (sourcePaths.length === 1) return sourcePaths[0];
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {sourcePaths.map((p: string, i: number) => (
                <Chip key={i} label={p} size="small" variant="outlined" />
              ))}
            </Stack>
          );
        },
      },
      {
        // QA #108: which File Watcher runs this schedule.
        field: 'watcher_name',
        headerName: '綁定 File Watcher',
        flex: 0.9,
        minWidth: 140,
        valueGetter: (_value, row) => row.watcher_name || (row.watcher_id ? '已刪除的 Watcher' : '未綁定'),
      },
      {
        field: 'cron_expression',
        headerName: '頻率',
        width: 120,
        renderCell: (params) => (
          <Tooltip title={params.value} arrow>
            <Chip label={describeFrequency(params.value)} size="small" />
          </Tooltip>
        ),
      },
      {
        field: 'cron_time',
        headerName: '執行時間',
        width: 120,
        valueGetter: (_value, row) => row.cron_expression,
        renderCell: (params) => describeTime(params.value),
      },
      {
        field: 'is_enabled',
        headerName: '狀態',
        width: 120,
        type: 'boolean',
        renderCell: (params) => (
          <Tooltip title={params.value ? '啟用中，點擊停用' : '已停用，點擊啟用'} arrow>
            <span>
              <Switch
                size="small"
                color="success"
                checked={Boolean(params.value)}
                disabled={togglingId === params.row.id}
                onChange={(e) => toggleEnabled(params.row, e.target.checked)}
                inputProps={{ 'aria-label': params.value ? '停用排程' : '啟用排程' }}
              />
            </span>
          </Tooltip>
        ),
      },
      {
        field: 'actions',
        headerName: '操作',
        // ADR-020 Addendum: 容納 3 顆 button (執行 + 編輯 + 刪除) + spacing；
        // 原 width 150 + flex-end + overflow:hidden 把「執行」切掉。
        width: 240,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={<PlayArrowIcon />}
              onClick={() => openTriggerDialog(params.row.id, params.row.name)}
            >
              執行
            </Button>
            <Button size="small" variant="outlined" onClick={() => startEdit(params.row)}>
              編輯
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => openDeleteDialog(params.row.id)}
            >
              刪除
            </Button>
          </Stack>
        ),
      },
    ],
    [togglingId],  // Switch 的 disabled 讀這個 state；其餘 handler 只呼叫 setter/loadSchedules
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        自動備份時間設定
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        設定定時備份排程
      </Typography>

      {(currentUser?.role === 'admin' || currentUser?.role === 'sub_admin') && (
        <FormControl size="small" sx={{ minWidth: 160, mt: 2 }}>
          <InputLabel>使用者篩選</InputLabel>
          <Select
            value={userFilter}
            label="使用者篩選"
            onChange={(e) => { setUserFilter(e.target.value); }}
          >
            <MenuItem value="">全部使用者</MenuItem>
            {availableUsers.map((u) => (
              <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          備份排程設定
        </Typography>

        <Grid container spacing={2} mt={1}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="排程名稱"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如：每日文件備份"
              required
              {...form.bind('name')}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom color={form.errors.source_paths ? 'error' : undefined}>
              備份路徑 *
            </Typography>
            {form.errors.source_paths && (
              <FormHelperText error sx={{ mb: 1 }}>{form.errors.source_paths}</FormHelperText>
            )}
            {paths.map((p, i) => (
              <Stack direction="row" spacing={1} key={i} sx={{ mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  name="source_paths"
                  value={p}
                  onChange={(e) => updatePath(i, e.target.value)}
                  onInput={() => form.clearField('source_paths')}
                  placeholder="/path/to/backup"
                  error={Boolean(form.errors.source_paths)}
                />
                <IconButton onClick={() => removePath(i)} disabled={paths.length <= 1} size="small">
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addPath}>
              新增路徑
            </Button>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required error={Boolean(form.errors.watcher_id)}>
              <InputLabel>綁定 File Watcher</InputLabel>
              <Select
                name="watcher_id"
                value={selectedWatcherId}
                label="綁定 File Watcher"
                onChange={(e) => {
                  setSelectedWatcherId(e.target.value);
                  form.clearField('watcher_id');
                }}
              >
                {watchers.map((w) => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))}
                {/* QA #108: the edited schedule's watcher stays visible even
                    when it is disabled (the picker lists enabled ones only). */}
                {boundWatcherMissing && (
                  <MenuItem value={selectedWatcherId} disabled>
                    {editingRow?.watcher_name || '已刪除的 Watcher'}（已停用，請改選其他 Watcher）
                  </MenuItem>
                )}
                {watchers.length === 0 && (
                  <MenuItem value="" disabled>尚無可用的 Watcher</MenuItem>
                )}
              </Select>
              {form.errors.watcher_id && <FormHelperText>{form.errors.watcher_id}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>備份頻率</InputLabel>
              <Select
                value={frequency}
                label="備份頻率"
                onChange={(e) => setFrequency(e.target.value as Frequency)}
              >
                <MenuItem value="daily">每天</MenuItem>
                <MenuItem value="weekly">每週（週日）</MenuItem>
                <MenuItem value="monthly">每月（1 號）</MenuItem>
                <MenuItem value="custom">自訂（自選星期幾）</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* QA #47: 自訂 = 一週內挑幾天執行；沒有選項的「自訂」以前只是每天 */}
          {frequency === 'custom' && (
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                執行日（每週）
              </Typography>
              <ToggleButtonGroup
                value={customDays}
                onChange={(_e, next: number[]) => {
                  setCustomDays(next);
                  if (next.length > 0) form.clearField('days');
                }}
                size="small"
                aria-label="執行日"
              >
                {WEEKDAY_LABELS.map((label, day) => (
                  <ToggleButton key={day} value={day} aria-label={`週${label}`} sx={{ px: 2 }}>
                    週{label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <FormHelperText error={Boolean(form.errors.days)}>
                {form.errors.days ?? `將於所選的每一天 ${hour}:${minute} 執行（${browserZone}）`}
              </FormHelperText>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Typography variant="body2" gutterBottom color={form.errors.cron_expression ? 'error' : undefined}>
              備份時間（{browserZone}）
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {/* The backend validates the whole cron_expression; a refusal is shown under the time row. */}
              <Select
                name="cron_expression"
                value={hour}
                onChange={(e) => {
                  setHour(e.target.value);
                  form.clearField('cron_expression');
                }}
                size="small"
                error={Boolean(form.errors.cron_expression)}
              >
                {HOURS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </Select>
              <Typography>時</Typography>
              <Select
                value={minute}
                onChange={(e) => {
                  setMinute(e.target.value);
                  form.clearField('cron_expression');
                }}
                size="small"
                error={Boolean(form.errors.cron_expression)}
              >
                {MINUTES.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
              <Typography>分</Typography>
            </Stack>
            {form.errors.cron_expression && <FormHelperText error>{form.errors.cron_expression}</FormHelperText>}
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="contained" onClick={handleSaveSchedule}>
                {editingId ? '更新排程' : '儲存設定'}
              </Button>
              {editingId && (
                <>
                  <Button variant="text" onClick={resetForm}>
                    取消編輯
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    正在編輯：{name || '（未命名）'}
                  </Typography>
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          目前排程
        </Typography>

        <DataGrid
          rows={schedules}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          autoHeight
          localeText={{ noRowsLabel: '暫無排程資料' }}
          slotProps={{
            pagination: {
              labelRowsPerPage: '每頁筆數：',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}-${to} / 共 ${count}`,
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            },
            // ADR-020 Addendum: actions cell 必須 overflow visible 否則 3 顆 button 被切。
            '& .MuiDataGrid-cell[data-field="actions"]': {
              justifyContent: 'flex-end',
              overflow: 'visible',
              whiteSpace: 'nowrap',
            },
          }}
        />
      </Paper>

      {/* 立即執行確認對話框 */}
      <Dialog
        open={triggerDialogOpen}
        onClose={() => !triggering && setTriggerDialogOpen(false)}
      >
        <DialogTitle>確認執行排程</DialogTitle>
        <DialogContent>
          <Typography>
            確定要立即執行排程「{scheduleToTrigger?.name}」嗎？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriggerDialogOpen(false)} disabled={triggering}>
            取消
          </Button>
          <Button
            onClick={handleTriggerSchedule}
            color="warning"
            variant="contained"
            disabled={triggering}
            startIcon={triggering ? <CircularProgress size={16} /> : undefined}
          >
            {triggering ? '執行中...' : '確認執行'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 刪除排程確認對話框 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>確認刪除排程</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除此排程嗎？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            取消
          </Button>
          <Button
            onClick={handleDeleteSchedule}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? '刪除中...' : '確認刪除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
